import { Box, Grid2 as Grid, Paper, Stack } from "@mui/material";
import { useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import Postview from "./files/postview";
import Message from "./message";
import Header from "./header";

function Chat() {
  const listRef = useRef(null);
  const lastFetchedPage = useRef(0);
  const isInitialLoad = useRef(true);
  const [data, setData] = useState({});
  const previousScrollHeight = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const { socket, chat } = useSelector((state) => state.socketSlice);

  // Memoized getMessages function
  const getMessages = useCallback((roomId, search = "", limit = 20, currentPage = 1) => {
    if (!socket) return;

    socket.emit("get-messages", {
      roomId,
      search,
      limit,
      currentPage
    });
  }, [socket]);

  // Load initial messages when chat changes
  useEffect(() => {
    if (chat?._id && socket) {
      setData({});
      setIsLoading(true);
      isInitialLoad.current = true;
      lastFetchedPage.current = 0;
      getMessages(chat._id, "", 20, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat?._id, socket]);

  // Infinite scroll - load more messages when scrolling to top
  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement) return;

    let isScrolling = false;

    const handleScroll = () => {
      // Prevent multiple simultaneous calls
      if (isScrolling || isLoading || !data?.pagination?.hasNextPage) {
        return;
      }

      // Check if scrolled near the top (within 100px)
      if (listElement.scrollTop <= 100) {
        isScrolling = true;
        setIsLoading(true);

        // Store current scroll height and position
        previousScrollHeight.current = listElement.scrollHeight;

        // Load next page
        getMessages(chat?._id, "", 20, data?.pagination?.currentPage + 1);

        // Reset flag after a delay to prevent rapid firing
        setTimeout(() => {
          isScrolling = false;
        }, 1000);
      }
    };

    listElement.addEventListener("scroll", handleScroll);
    return () => {
      listElement.removeEventListener("scroll", handleScroll);
    };
  }, [chat?._id, data?.pagination, isLoading, getMessages]);

  // Socket event listeners
  useEffect(() => {
    if (!socket) return;

    const handleMessagesList = ({ data: responseData }) => {
      const currentPage = responseData?.pagination?.currentPage;

      // Prevent processing duplicate responses for the same page
      if (currentPage === lastFetchedPage.current) {
        return;
      }

      lastFetchedPage.current = currentPage;

      // Log message IDs or timestamps to debug

      const formatted = {
        pagination: { ...responseData?.pagination },
        records: [...responseData?.records?.reverse()]
      };

      setData(prev => {
        // If it's the first page, replace data
        if (currentPage === 1) {
          return formatted;
        }

        // If loading more (pagination), prepend OLDER messages to the TOP
        const updated = {
          pagination: formatted.pagination,
          records: [...formatted.records, ...(prev?.records || [])]
        };
        return updated;
      });

      setIsLoading(false);

      // Restore scroll position after loading more messages (not initial load)
      if (responseData?.pagination?.currentPage > 1 && listRef.current) {
        setTimeout(() => {
          const listElement = listRef.current;
          if (listElement) {
            const newScrollHeight = listElement.scrollHeight;
            const scrollDifference = newScrollHeight - previousScrollHeight.current;
            listElement.scrollTop = scrollDifference;
          }
        }, 50);
      }

      // Scroll to bottom only on initial load
      if (isInitialLoad.current && listRef.current) {
        setTimeout(() => {
          if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
            isInitialLoad.current = false;
          }
        }, 100);
      }
    };

    socket.on("messages-list", handleMessagesList);

    socket.on("receive-message", ({ message }) => {
      setData(prev => ({
        ...prev,
        records: [...(prev?.records || []), message]
      }));

      // Auto-scroll to bottom on new message if already near bottom
      setTimeout(() => {
        if (listRef.current) {
          const listElement = listRef.current;
          const isNearBottom = listElement.scrollHeight - listElement.scrollTop - listElement.clientHeight < 100;

          if (isNearBottom) {
            listElement.scrollTop = listElement.scrollHeight;
          }
        }
      }, 50);
    });

    socket.on("message-sent", ({ message }) => {
      setData(prev => ({
        ...prev,
        records: [...(prev?.records || []), message]
      }));

      // Always scroll to bottom on sent message
      setTimeout(() => {
        if (listRef.current) {
          listRef.current.scrollTop = listRef.current.scrollHeight;
        }
      }, 50);
    });

    return () => {
      socket.off("message-sent");
      socket.off("receive-message");
      socket.off("messages-list", handleMessagesList);
    };
    // eslint-disable-next-line
  }, [socket]);

  return (
    <Grid size={{ xs: 12, md: "grow" }}>
      <Paper
        sx={{
          p: 0,
          width: "100%",
          display: "grid",
          borderRadius: "12px",
          minWidth: { xs: 300, sm: 450 },
          gridTemplateRows: "auto 1fr auto"
        }}
        variant="outlined"
      // paperVariant="dashboard"
      >
        {Boolean(Object.keys(chat)?.length) ? (
          <>
            <Header />
            <Stack
              p={1}
              ref={listRef}
              spacing={1.5}
              bgcolor="#f0f0f0"
              className="scrollbar-hidden"
              sx={{
                overflowY: "auto",
                // maxHeight: checkUserType() === "member" ? 473 : 791
              }}
            >
              {isLoading && data?.pagination?.currentPage > 1 && (
                <Box sx={{ textAlign: "center", py: 1, color: "#666" }}>
                  Loading more messages...
                </Box>
              )}

              {Boolean(data?.records?.length) ?
                data?.records?.map((element, i) => (
                  element?.files?.length ?
                    <Postview
                      socket={socket}
                      message={element}
                      key={element?._id || i}
                      isSender={element?.sender !== chat?.user?._id}
                    /> :
                    <Message
                      socket={socket}
                      message={element}
                      key={element?._id || i}
                      isSender={element?.sender !== chat?.user?._id}
                    />
                ))
                : !isLoading ?
                  <Box
                    sx={{
                      fontSize: 20,
                      display: "grid",
                      fontWeight: "500",
                      placeContent: "center",
                    }}
                  >No messages yet.
                  </Box> :
                  null
              }
            </Stack>
          </>
        ) : (
          <>
            <Box />
            <Box
              sx={{
                fontSize: 20,
                display: "grid",
                fontWeight: "500",
                bgcolor: "#f0f0f0",
                placeContent: "center",
              }}
            >
              Select a chat
            </Box>
          </>
        )}
      </Paper>
    </Grid>
  );
}

export default Chat;